/* global ChoiceOption */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
import React, { useReducer, useEffect, useRef } from 'react';
import { Column } from 'react-table';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import ControlPointTwoToneIcon from '@mui/icons-material/ControlPointTwoTone';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import {
  getNewChips,
  createAChip,
  createChips,
  deserializeColumns,
  formatFiltersForQuery,
  setAnchorForChip,
  parameterizeFilters,
  persistToLocalStorage,
  buildFiltersFromURLParams,
} from './helpers';
import { FilterChip, SystemTypeEnum } from '../../../record-viewer/types';
import { SqlOptions } from '../../../tasks/types';
import ColumnFilterValues from './column-filter-values';
import ColumnFilterMenu from './column-filter-menu';
import { useApp } from '../../../hooks/use-app';
import Button from '../../../components/button';

interface Props {
  formData: SqlOptions;
  columns: Column[];
  callback: (filter: Record<string, any>[]) => void
  type: SystemTypeEnum;
  resetFilters: boolean
}

interface State {
  columnDataChips: FilterChip[];
  categoryMenu: boolean;
  category: string;
  anchor: HTMLButtonElement | null;
}

const StyledButtonTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(1.75),
  lineHeight: theme.spacing(3),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  maxWidth: 224,
}));

const ColumnFilter: React.FC<Props> = ({
  formData, columns, callback, type, resetFilters,
}) => {
  const cols: any = deserializeColumns(columns);
  const currentTargetRef = useRef(null);
  const form = formData.restForm;
  const { api } = useApp();

  const [state, setState] = useReducer(
    (oldState, newState) => ({ ...oldState, ...newState }),
    {
      columnDataChips: [],
      categoryMenu: false,
      category: '',
      anchor: null,
    } as State,
  );

  const {
    columnDataChips, categoryMenu, category, anchor,
  } = state;

  useEffect(() => {
    generateFiltersFromParams();
  }, []);

  useEffect(() => {
    if (category) {
      setState({ anchor: anchor ?? currentTargetRef.current });
    }
  }, [category]);

  useEffect(() => {
    persistToLocalStorage(type as SystemTypeEnum, columnDataChips);
  }, [columnDataChips]);

  useEffect(() => {
    if (resetFilters) {
      parameterizeFilters(cols, [], form.system_type);
      setState({ columnDataChips: [] });
    }
  }, [resetFilters]);

  async function generateFiltersFromParams() {
    const filtersFromParams = await buildFiltersFromURLParams(
      api,
      cols,
      form.id,
      type as SystemTypeEnum,
    );

    const formattedFilters = formatFiltersForQuery(filtersFromParams, type);

    callback(formattedFilters);
    setState({ columnDataChips: filtersFromParams });
  }

  function handleDeleteFilter(chip: FilterChip) {
    const chips: FilterChip[] = columnDataChips.filter(
      (colChip: FilterChip) => !_.isEqual(colChip, chip),
    );

    setState({ columnDataChips: chips });
    callback(formatFiltersForQuery(chips, type));

    if (form) {
      parameterizeFilters(cols, chips, form.system_type);
    }
  }

  function handleChangeColumn(event: React.MouseEvent<HTMLLIElement>) {
    const { filterCategory } = (event.target as HTMLLIElement).dataset;
    const chips = getNewChips(cols, columnDataChips, filterCategory);

    setState({
      category: filterCategory,
      categoryMenu: false,
      columnDataChips: chips,
      anchor: setAnchorForChip(chips, columnDataChips, anchor),
    });
  }

  function handleSave(filters: ChoiceOption[]) {
    const chip = createAChip(cols, filters, category);
    const chips = createChips(columnDataChips, chip, category);

    callback(formatFiltersForQuery(chips, type));

    setState({
      columnDataChips: chips,
      categoryMenu: false,
      category: '',
    });

    if (form) {
      parameterizeFilters(cols, chips, form.system_type);
    }
  }

  const handleClose = () => {
    const chips = columnDataChips
      .filter((chip: FilterChip) => (chip.values as ChoiceOption[]).length);

    setState({
      category: '',
      categoryMenu: false,
      columnDataChips: chips,
    });
  };

  function handleOpen(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setState({ anchor: e.currentTarget, categoryMenu: true });
  }

  const menuProps = {
    anchor,
    categoryMenu,
    columns: _.sortBy(cols, 'header'),
    onClose: handleClose,
    onSelectColumn: handleChangeColumn,
  };

  const valueProps = {
    type,
    formData,
    anchor,
    category,
    columnDataChips,
    onClose: handleClose,
    onSave: handleSave,
  };

  function renderFilters() {
    return (
      <>
        <Button
          sx={{ margin: 0.5 }}
          aria-haspopup="true"
          variant="text"
          onClick={handleOpen}
          startIcon={<ControlPointTwoToneIcon fontSize="small" />}
        >
          <StyledButtonTitle>Add Filter</StyledButtonTitle>
        </Button>
        <ColumnFilterMenu {...menuProps} />
        {(anchor && category) && <ColumnFilterValues {...valueProps} />}
      </>
    );
  }

  return (
    <>
      {columnDataChips.map((chip: FilterChip) => (
        <StyledChip
          key={chip.key}
          ref={currentTargetRef}
          label={chip.label}
          title={chip.label}
          variant="outlined"
          onDelete={() => handleDeleteFilter(chip)}
          onClick={(e) => setState({ category: chip.key, anchor: e.currentTarget })}
        />
      ))}
      {renderFilters()}
    </>
  );
};

// memoize the component so it only re-renders if the callback or form changes
export default React.memo(ColumnFilter);
